// ── Trailing-slash guard ─────────────────────────────────────────────────────
// Patch history.pushState / replaceState BEFORE any library loads so that
// trailing-slash URLs never enter the history stack.  Stackflow's
// historySyncPlugin uses history.back() to pop activities — duplicate entries
// (/messages/uuid/ vs /messages/uuid) cause an extra "blank" step.
{
  const _push = history.pushState.bind(history);
  const _replace = history.replaceState.bind(history);

  function stripSlash(url: string | URL | null | undefined) {
    if (typeof url !== 'string') return url;
    const qIdx = url.indexOf('?');
    const hIdx = url.indexOf('#');
    const end = Math.min(
      qIdx >= 0 ? qIdx : url.length,
      hIdx >= 0 ? hIdx : url.length,
    );
    const path = url.slice(0, end);
    if (path !== '/' && path.endsWith('/')) {
      return path.slice(0, -1) + url.slice(end);
    }
    return url;
  }

  history.pushState = (state, title, url) => _push(state, title, stripSlash(url));
  history.replaceState = (state, title, url) => _replace(state, title, stripSlash(url));

  // Also fix the current URL if the page was loaded with a trailing slash
  if (location.pathname !== '/' && location.pathname.endsWith('/')) {
    _replace(history.state, '', location.pathname.slice(0, -1) + location.search + location.hash);
  }
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { platform } from '@/utils/platform';
import { initCapacitorPlugins } from '@/capacitor/init';
import { initNativeGoogleAuth } from '@/capacitor/plugins/google-auth';
import './i18n';
import App from './App';
import { AppErrorBoundary } from './components/error/AppErrorBoundary';
import './styles/index.css';

// Register service worker for PWA installability (web only)
if ('serviceWorker' in navigator && platform.isWeb) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // SW registration failed — non-critical
    });
  });
}

// Initialize Capacitor plugins on native platforms
async function bootstrap() {
  console.log(`[App] Bootstrap — platform: ${platform.current}, native: ${platform.isNative}`);
  console.log(`[App] API_URL: ${import.meta.env.VITE_API_URL}`);
  console.log(`[App] APP_ENV: ${import.meta.env.VITE_APP_ENV}`);

  await initCapacitorPlugins();

  // Initialize native Google Auth via @capgo/capacitor-social-login
  console.log('[App] Initializing native Google Auth...');
  await initNativeGoogleAuth(
    import.meta.env.VITE_GOOGLE_CLIENT_ID,
    import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID,
  );

  // Camera plugin browser fallback (file picker on web)
  defineCustomElements(window);

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>
  );
}

bootstrap();
